import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { SearchComponent } from '@client-monorepo/common/ui-components';
import { AssetsSliderComponent } from '@client-monorepo/common/user-assets';
import { ActivatedRoute, Router } from '@angular/router';
import { Banner, BannerResponse, BannersApiService } from '@client-monorepo/libs/shared/common/banners';
import { ScrolledToEndDirective } from '@client-monorepo/common/utilities';
import { PromotionApiService, PromotionGroupInterface } from '@client-monorepo/common/promotions';
import { BackHandlerService } from '@client-monorepo/back-handler';
import { OnlineStoresComponent } from '../../components/online-stores/online-stores.component';
import { OnsiteStoresComponent } from '../../components/onsite-stores/onsite-stores.component';
import { SegmentItemsModel } from '@digipay/ngx-segmented-control/lib/models/types';
import { NgxSegmentedControlComponent } from '@digipay/ngx-segmented-control';

@Component({
  selector: 'stores-applet-main',
  standalone: true,
  imports: [
    CommonModule,
    AssetsSliderComponent,
    SearchComponent,
    ScrolledToEndDirective,
    OnlineStoresComponent,
    OnsiteStoresComponent,
    NgxSegmentedControlComponent,
  ],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainComponent implements OnInit {
  // Injections
  backHandler = inject(BackHandlerService);
  bannersApiService = inject(BannersApiService);
  promotionApiService = inject(PromotionApiService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  location = inject(Location);

  // Variables
  tabOptions: SegmentItemsModel[] = [
    {
      id: 0,
      text: 'فروشگاه‌های آنلاین',
      value: 'online',
    },
    {
      id: 1,
      text: 'فروشگاه‌های حضوری',
      value: 'onsite',
    },
  ];
  selectedTab = computed(() => this.tabOptions.find((t) => t.value === this.mode()));
  mode = signal<'online' | 'onsite'>('online');
  bannersData = signal<Banner[]>([]);
  promotionGroups = signal<Array<PromotionGroupInterface>>([]);
  lastDeferBlockRendered = -1;
  shouldSkipDeferLoading = signal<boolean>(false);
  canLoadOnSiteStores = signal(false);

  ngOnInit(): void {
    this.getBanners();
    this.getPromotionGroups();
    this.changeModeBasedOnQueryParam();
    this.changeQueryParamBasedOnMode();
  }

  private getBanners(): void {
    const data = this.route.snapshot.data['initialData'] as BannerResponse;
    if (data) {
      this.bannersData.set(data.banners);
    } else {
      this.bannersApiService.getBanners().subscribe({
        next: (result) => {
          this.bannersData.set(result.banners);
        },
      });
    }
  }

  goToSearch(event: any): void {
    this.router.navigate(['/stores/search']);
  }

  getPromotionGroups(): void {
    this.promotionApiService.getPromotionGroupList().subscribe({
      next: (promotions) => {
        this.promotionGroups.set(promotions);
      },
    });
  }

  changeQueryParamBasedOnMode(): void {
    const mode = this.route.snapshot.queryParams['mode'];
    const url = this.router.url;
    if (mode) {
      this.location.go(url.substring(0, url.indexOf('?')) + '?mode=' + this.mode());
    } else {
      this.location.go(url + '?mode=' + this.mode());
    }
  }

  changeModeBasedOnQueryParam(): void {
    const mode = this.route.snapshot.queryParams['mode'];
    if (mode === 'onsite') {
      this.mode.set('onsite');
      this.canLoadOnSiteStores.set(true);
    } else {
      this.mode.set('online');
    }
  }

  changeStoresMode(event: SegmentItemsModel) {
    this.canLoadOnSiteStores.set(true);
    const mode = event.value === 'online' ? 'online' : 'onsite';
    this.mode.set(mode);
    this.changeQueryParamBasedOnMode();
  }
}
